<template>
  <div>
    <FlatActionButton
      :action="handleFetchAgentLog"
      :buttonClass="['btnDefault', 'btnCTA']"
      :disabled="cannotFetchLogs"
      :text="'Get agent log'"
    />
    <IconTooltip>
      Download up to 1MB in agent logs to help troubleshoot any problems.
      Agent logs can only be retrieved if the device is online, and it may
      take a few minutes to retrieve results.
    </IconTooltip>
    <div
      v-if="retrievingAgentLog"
      :class="$style.retrievingAgentLog"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" :class=" $style.loader" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="currentColor" d="M27.662 6.749a3.242 3.242 0 11-6.336 1.38 3.242 3.242 0 016.336-1.38zm-7.974-2.806a2.976 2.976 0 11-5.816 1.268 2.976 2.976 0 015.816-1.268zm-7.81 2.435a2.71 2.71 0 11-5.296 1.156 2.71 2.71 0 015.296-1.156zm-4.661 6.484a2.444 2.444 0 11-4.778 1.042 2.444 2.444 0 014.779-1.042zm-.115 7.566a2.179 2.179 0 11-4.258.928 2.179 2.179 0 014.258-.928zm3.06 4.927a1.913 1.913 0 11-3.739.815 1.913 1.913 0 013.739-.815zm5.634 3.046a1.648 1.648 0 11-3.22.702 1.648 1.648 0 013.22-.702zm6.654-.596a1.382 1.382 0 11-2.7.59 1.382 1.382 0 012.7-.59zm5.515-4.633a1.116 1.116 0 11-2.182.474 1.116 1.116 0 012.182-.474zm1.632-7.24a.85.85 0 11-1.662.362.85.85 0 011.662-.362z"/></svg>
      Retrieving information may take several minutes.
    </div>
    <div
      v-if="isAgentLogPresent"
      :class="$style.downloadAgentLog"
    >
      <a
        :download="agentLogDownloadFilename"
        :href="agentLogDownloadLink"
        @click="handleLogDownloadClick"
      >
        <DownloadIcon :class="$style.downloadIcon" />
        {{ agentLogDownloadText }}
      </a>
    </div>
  </div>
</template>
<script>
import { get } from 'lodash';
import { mapActions, mapState } from 'vuex';
import { notify } from 'client/util';
import CommandsApi from 'client/api/v2/CommandsApi';
import Constants, { ItemTypeConsts, LocalStorageConsts, StoreModelMap } from 'client/util/Constants';
import DownloadIcon from 'public/img/icons/download-icon.svg?inline';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import IconTooltip from 'client/components/Tooltip/IconTooltip.vue';
import SessionStorageService from 'client/util/SessionStorageService';
import Util from 'client/util/Util';
import moment from 'moment';

const systemModel = StoreModelMap[ItemTypeConsts.system];
const { error } = Constants;
const { agentLogSavedData } = LocalStorageConsts;

export default {
  name: 'SystemAgentLogs',

  components: {
    DownloadIcon,
    FlatActionButton,
    IconTooltip,
  },

  data: () => ({
    isAgentLogPresent: false,
    retrievingAgentLog: false,
    agentLogData: {},
  }),

  EventHelper,
  SessionStorageService,
  CommandsApi,

  computed: {
    ...mapState(systemModel, {
      item: state => state.item,
    }),

    agentLogDownloadFilename() {
      const responseTime = get(this.agentLogData, 'responseTime', null);
      if (responseTime) {
        return `jcagent_${responseTime.format('YYYY-MM-DD_HH-mm')}.log`;
      }

      return '';
    },

    agentLogDownloadLink() {
      const output = get(this.agentLogData, 'output', null);
      if (output) {
        return `data:text/plain;charset=utf-8,${encodeURIComponent(output)}`;
      }

      // eslint-disable-next-line no-script-url
      return 'javascript:void(0)';
    },

    agentLogDownloadText() {
      const responseTime = get(this.agentLogData, 'responseTime', null);
      if (responseTime) {
        return `Download logs ${this.agentLogData.responseTime.format('L')}`;
      }

      return '';
    },

    cannotFetchLogs() {
      return !this.item.active || this.retrievingAgentLog;
    },
  },

  mounted() {
    // check if this user has tried to fetch the agent log
    // use session storage
    this.agentLogData = Util.tryParseJson(
      this.$options.SessionStorageService.getItem(agentLogSavedData),
    );
    if (this.agentLogData && this.agentLogData[this.item.id]) {
      this.fetchAgentLogData(this.agentLogData[this.item.id]);
    }

    this.item.getProcessingData = this.getModalProps;
  },

  methods: {
    ...mapActions(systemModel, [
      'runAgentLogCommand',
      'updateItem',
    ]),

    fetchAgentLogData(commandId) {
      return this.$options.CommandsApi.getMostRecentResult(commandId)
        .then(resp => this.parseAgentLogData(resp))
        .catch(() => this.removeAgentLogDataFromStorage());
    },

    getModalProps() {
      return {
        modalProps: {
          headerText: 'Are you sure you want to leave?',
          mdTemplate: 'We’re currently retrieving this device’s logs, and it may take a few minutes.\n\nNOTE: If you choose to leave, the logs won\'t be retrieved. Are you sure you want to navigate away?',
        },
      };
    },

    handleFetchAgentLog() {
      this.retrievingAgentLog = true;
      this.updateItem({ isProcessing: true });
      this.isAgentLogPresent = false; // hide if there is a log present

      this.$options.EventHelper.triggerSystemGetAgentLogClicked();

      this.removeAgentLogDataFromStorage();
      return this.runAgentLogCommand()
        .then(commandResult => this.parseAgentLogData(commandResult))
        .then(commandId => this.$options.CommandsApi.delete(commandId))
        .catch(() => {
          notify(error, 'An error occurred while attempting to retrieve the log.');
        })
        .finally(() => {
          this.retrievingAgentLog = false;
          this.updateItem({ isProcessing: false });
        });
    },

    handleLogDownloadClick() {
      this.$options.EventHelper.triggerSystemDownloadAgentLogClicked();
    },

    parseAgentLogData(commandResult) {
      const data = get(commandResult, 'response.data');
      const commandId = commandResult.workflowId;

      if (!data || data.exitCode !== 0) {
        notify(error, 'An error occurred while attempting to retrieve the log.');
      } else {
        const savedData = Util.tryParseJson(
          this.$options.SessionStorageService.getItem(agentLogSavedData),
        );

        if (!savedData[this.item.id] || savedData[this.item.id] !== commandId) {
          savedData[this.item.id] = commandResult.workflowId;
          this.$options.SessionStorageService.setItem(
            agentLogSavedData, JSON.stringify(savedData),
          );
        }

        this.agentLogData = { ...data, responseTime: moment(commandResult.responseTime) };
        this.isAgentLogPresent = true;
      }

      return commandId;
    },

    removeAgentLogDataFromStorage() {
      // remove this data for this system from the sessionStorage data
      // use session storage
      const savedData = Util.tryParseJson(
        this.$options.SessionStorageService.getItem(agentLogSavedData),
      );
      if (savedData[this.item.id]) {
        delete savedData[this.item.id];
        this.$options.SessionStorageService.setItem(agentLogSavedData, JSON.stringify(savedData));
      }
    },
  },
};
</script>
<style module>
.downloadAgentLog {
  display: inline-block;
  padding-left: 0.5em;
}

.downloadIcon {
  fill: currentColor;
  height: 1em;
  vertical-align: middle;
  width: 1em;
}

.loader {
  animation: spin 2s linear infinite;
  fill: var(--jcPrimary);
  height: 1em;
  width: 1em;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.retrievingAgentLog {
  display: inline-block;
  font-style: italic;
  padding-left: 0.5em;
}
</style>
